/**
 * Init script that installs a persistent fake-microphone bus and patches
 * `navigator.mediaDevices.getUserMedia` to route audio through it.
 *
 * Validated by apps/api/scripts/fake-mic-spike.ts
 *
 * - One AudioContext + MediaStreamAudioDestinationNode per document.
 * - `window.__fakeMic.playBase64Wav(b64, { loop })` decodes a WAV and connects
 *   a BufferSource into the shared destination; apps that already have the
 *   cached audio track from a prior getUserMedia call hear the new clip
 *   without needing to re-invoke getUserMedia.
 * - Video tracks are sourced from the real (Chromium fake) device when an app
 *   requests audio+video together.
 */
export const FAKE_MIC_SCRIPT = `(() => {
  try {
    if (!navigator.mediaDevices) return;
    if (navigator.mediaDevices.__canaryFakeMicPatched) return;
    var originalGetUserMedia = navigator.mediaDevices.getUserMedia
      ? navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices)
      : null;
    var state = { ctx: null, dest: null, sources: [] };

    function ensureBus() {
      if (!state.ctx) {
        var Ctor = window.AudioContext || window.webkitAudioContext;
        state.ctx = new Ctor();
        state.dest = state.ctx.createMediaStreamDestination();
      }
      if (state.ctx.state !== 'running') {
        state.ctx.resume().catch(function () {});
      }
      return state.dest.stream;
    }

    function b64ToArrayBuffer(b64) {
      var bin = atob(b64);
      var bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes.buffer;
    }

    async function playBase64Wav(b64, opts) {
      var loop = !!(opts && opts.loop);
      ensureBus();
      var ab = b64ToArrayBuffer(b64);
      var audioBuffer = await state.ctx.decodeAudioData(ab.slice(0));
      var src = state.ctx.createBufferSource();
      src.buffer = audioBuffer;
      src.loop = loop;
      src.connect(state.dest);
      src.onended = function () {
        try { src.disconnect(); } catch (e) {}
        state.sources = state.sources.filter(function (s) { return s !== src; });
      };
      state.sources.push(src);
      src.start();
      return { duration: audioBuffer.duration, sampleRate: audioBuffer.sampleRate };
    }

    function stopAll() {
      for (var i = 0; i < state.sources.length; i++) {
        try { state.sources[i].stop(); } catch (e) {}
        try { state.sources[i].disconnect(); } catch (e) {}
      }
      state.sources = [];
    }

    Object.defineProperty(window, '__fakeMic', {
      value: { ensure: ensureBus, playBase64Wav: playBase64Wav, stopAll: stopAll },
      configurable: true,
    });

    Object.defineProperty(navigator.mediaDevices, 'getUserMedia', {
      configurable: true,
      writable: true,
      value: async function (constraints) {
        var wantsAudio = !!(constraints && constraints.audio);
        var wantsVideo = !!(constraints && constraints.video);
        if (!wantsAudio) {
          if (!originalGetUserMedia) throw new Error('getUserMedia not available');
          return originalGetUserMedia(constraints);
        }
        var fakeStream = ensureBus();
        var tracks = [].slice.call(fakeStream.getAudioTracks());
        if (wantsVideo && originalGetUserMedia) {
          try {
            var vidOnly = await originalGetUserMedia({ video: constraints.video, audio: false });
            var vTracks = vidOnly.getVideoTracks();
            for (var i = 0; i < vTracks.length; i++) tracks.push(vTracks[i]);
          } catch (e) {}
        }
        return new MediaStream(tracks);
      },
    });

    Object.defineProperty(navigator.mediaDevices, '__canaryFakeMicPatched', {
      value: true,
      configurable: false,
    });
  } catch (err) {
    try { console.warn('[canary] fake-mic bootstrap failed', err); } catch (e) {}
  }
})();`;
